import {memo, useMemo, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {useTheme, useMediaQuery} from '@mui/material'

import {useNavigate} from 'react-router-dom'

import {day, dayOfMonth, month, formatMoney} from '../../utils'

import EventCardButtons from './EventCardButtons'
import {Image, FavoriteIconButton} from '../generics'


// Desktop styles
const desktopStyles = ({theme}) => ({
  card: css`
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    border-radius: 6px;
    cursor: pointer;
    overflow: hidden;
    transition: 0.3s;
    box-shadow:
      rgba(0, 0, 0, 0.3) 0px 1px 2px 0px,
      rgba(0, 0, 0, 0.15) 0px 1px 3px 1px;
  `,
  imageContainer: css`
    width: 100%;
    border-radius: 6px 6px 0 0;
    position: relative;
    flex-shrink: 0;
  `,
  image: css`
    width: 100%;
    aspect-ratio: 3 / 4;
    object-fit: cover;
  `,
  infoContainer: css`
    padding: 12px 12px 0 12px;
    flex-grow: 1;
    display: flex;
    min-width: 0;
    height: 94px;
    max-height: 94px;
    overflow: hidden;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  `,
  title: css`
    width: 100%;
    max-width: 100%;
    font-weight: 600;
    font-size: 1rem;
    line-height: 1.2;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `,
  location: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 1rem;
    max-width: 100%;
    color: #ff0095;
    font-weight: 500;
  `,
  buttons: css`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    height: 44px;
    padding: 4px;
  `,
  overlay: css`
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 15px;
    left: 15px;
    width: 65px;
    height: 75px;
    background-color: white;
    border-radius: 5px;
    padding: 5px;
    box-shadow:
      rgba(0, 0, 0, 0.3) 0px 1px 2px 0px,
      rgba(0, 0, 0, 0.15) 0px 1px 3px 1px;
  `,
  dayAndMonth: css`
    font-size: 0.75rem;
    color: #303030;
    text-transform: uppercase;
  `,
  day: css`
    font-weight: 900;
    font-size: 1.6rem;
    line-height: 0.9;
  `,
  link: css`
    text-decoration: none;
    color: inherit;
  `,
  tags: css`
    overflow: hidden;
    display: flex;
    width: 100%;
  `
})

// Mobile styles
const mobileStyles = ({theme}) => ({
  card: css`
    position: relative;
    width: 100%;
    height: 160px;
    display: flex;
    padding: 10px 15px;
    cursor: pointer;
    transition: 0.3s;
  `,
  imageContainer: css`
    width: 110px;
    height: 140px;
    border-radius: 6px;
    position: relative;
    flex-shrink: 0;
    overflow: hidden;
    box-shadow:
      rgba(0, 0, 0, 0.3) 0px 1px 2px 0px,
      rgba(0, 0, 0, 0.15) 0px 1px 3px 1px;
  `,
  image: css`
    width: 100%;
    aspect-ratio: 3 / 4;
    object-fit: cover;
  `,
  infoContainer: css`
    padding: 12px;
    flex-grow: 1;
    display: flex;
    min-width: 0;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  `,
  title: css`
    width: 100%;
    font-weight: 500;
    font-size: 1.1rem;
    margin-bottom: 2px;
    overflow: hidden;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  `,
  location: css`
    font-size: 1rem;
    font-weight: 500;
  `,
  buttons: css`
    position: absolute;
    bottom: 0px;
    right: 0px;
    display: flex;
    align-items: center;
    padding: 10px;
  `,
  favoriteButton: css`
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 100;
  `,
  date: css`
    color: #ff0095;
    font-weight: 500;
  `,
  link: css`
    text-decoration: none;
    color: inherit;
  `,
  price: css`
    font-weight: 500;
  `
})



// // Extracted small component for the date badge
const DateInfographic = ({ start, styles }) => (
  <div css={styles.overlay}>
    <p css={styles.dayAndMonth}>{day(start)}</p>
    <h3 css={styles.day}>{dayOfMonth(start)}</h3>
    <p css={styles.dayAndMonth}>{month(start)}</p>
  </div>
)



const PerformanceCard = memo(({
  performance,
  editPerformanceWithId,
  deletePerformanceWithId,
  showStatsForPerformanceWithId
}) => {
  const navigate = useNavigate()
  
  const theme = useTheme()
  const isXs  = useMediaQuery((theme) => theme.breakpoints.only('xs'))


  const {id, attributes = {}} = performance || {}
  const {
    name,
    image,
    start,
    price,
    permission,
    address = {},
    imageDerivatives,
    externalImageUrl,
    externalImageSrcset
  } = attributes
  
  const {city, state} = address

  const imageURL = isXs ? imageDerivatives?.small  || image || externalImageUrl
                        : imageDerivatives?.medium || image || externalImageUrl


  // Pick styles based on screen size
  const styles = useMemo(() => {
    return isXs ? mobileStyles({theme}) : desktopStyles({theme})
  }, [isXs, theme])


  const handleNavigate = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/performances/${id}`)
  }, [navigate, id])



  return (
    <div css={styles.card}>
      <div css={styles.imageContainer} onClick={handleNavigate}>
        {!isXs && <DateInfographic start={start} styles={styles} />}
        
        <Image css={styles.image} src={imageURL} srcSet={externalImageSrcset} alt='event' />

        {isXs &&
          <div css={styles.favoriteButton}>
            <FavoriteIconButton id={id} key='favorite' type='performance' />
          </div>
        }
      </div>


      <div css={styles.infoContainer} onClick={handleNavigate}>
        <h3 css={styles.title}>{name}</h3>

        {isXs &&
          <p css={styles.date}>
            {`${day(start)}, ${month(start)} ${dayOfMonth(start)}`}
          </p>
        }
        <p css={styles.location}>
          {city || state}
        </p>

        {isXs &&
          <p css={styles.price}>
            {price > 0 && `${formatMoney(price)}`}
          </p>
        }
      </div>


      <div css={styles.buttons}>
        <EventCardButtons
          id={id}
          name={name}
          image={image || externalImageUrl}
          permission={permission}
          editPerformanceWithId={editPerformanceWithId}
          deletePerformanceWithId={deletePerformanceWithId}
          showStatsForPerformanceWithId={showStatsForPerformanceWithId}
        />
      </div>
    </div>
  )
})


export default memo(PerformanceCard)


